import { SellerTable, Column } from "../../../components/sellerDashboardTable";
import { CategorySelector } from "../../../components/TableSelector";
import { FilterCategory, FilterDropdown } from "../../../components/TreeSelector";
import styles from "./style.module.css";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { DeleteProductModal } from "../../../components/DeleteProductModal";
import Typography from "../../../components/Typography/Typography";
import { AddProductListModal } from "../../../components/AddProductListModal";
import { StatusSelector, Option } from "../../../components/StatusSelector";
import { OrderModal } from "../../../components/OrderModal";
import { OrderList } from "../../../components/OrderList";
import { exportIcon, filter, searchIcon } from "../../../assets/svgs";
import { useGetSellerOrdersQuery } from "../../../redux/services/orderApi";
import type { RootState } from "../../../redux/store";

interface OrderDisplay {
  id: string;
  fullId: string;
  created: string;
  customer: string;
  total: number;
  item: number;
  status: React.ReactNode;
  rawStatus: string;
}

export const OrderManagement = () => {
  const [search, setSearch] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddProductModal, setOpenAddProductModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [filterToggle, setFilterToggle] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Get selected store from Redux
  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  const statusMap: { [key: string]: string | undefined } = {
    'all': undefined,
    'pending': 'pending',
    'confirmed': 'confirmed',
    'processed': 'processed',
  };

  const {
    data: ordersData,
    isLoading,
    error,
    refetch,
  } = useGetSellerOrdersQuery({
    status: statusMap[selectedCategory],
    searchTerm: search || undefined,
    limit: itemsPerPage,
    currentPage: currentPage,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Refetch when store selection changes
  useEffect(() => {
    refetch();
  }, [selectedCategory, search, currentPage, selectedStoreId, refetch]);

  const handleAddModal = useCallback(() => {
    setOpenAddProductModal(prev => !prev);
  }, []);

  const handleDeleteModal = useCallback(() => {
    setOpenDeleteModal(prev => !prev);
  }, []);

  const handleOrderModal = useCallback(() => {
    setOrderModal(prev => !prev);
  }, []);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  };

  const getStatusLabel = (status: string): "Confirmed" | "Pending" | "Processed" => {
    if (!status) return "Pending";
    const s = status.toLowerCase();
    if (s === "confirmed") return "Confirmed";
    if (s === "pending") return "Pending";
    if (s === "processed") return "Processed";
    return "Pending";
  };

  const columns: Column<OrderDisplay>[] = useMemo(() => [
    { title: "ORDER ID", key: "id" },
    { title: "CREATED", key: "created" },
    { title: "CUSTOMER", key: "customer" },
    { title: "TOTAL", key: "total" },
    { title: "ITEM", key: "item" },
    { title: "STATUS", key: "status" },
  ], []);

  const getOptions: Option[] = useMemo(() => [
    { value: "Confirmed", label: "Confirmed" },
    { value: "Pending", label: "Pending" },
    { value: "Processed", label: "Processed" },
  ], []);

  const data: OrderDisplay[] = useMemo(() => {
    if (!ordersData?.data || ordersData.data.length === 0) return [];

    return ordersData.data.map((order) => {
      const statusLabel = getStatusLabel(order.status);
      return {
        id: `${order._id.slice(-4).toUpperCase()}`,
        fullId: order._id,
        created: formatDate(order.createdAt),
        customer: order.customer_name || "Unknown",
        total: order.total_amount,
        item: order.item_count,
        status: (
          <StatusSelector
            value={statusLabel}
            options={getOptions}
            onChange={(val) => console.log("Selected:", val)}
          />
        ),
        rawStatus: order.status.toLowerCase(),
      };
    });
  }, [ordersData, getOptions]);

  const categories = useMemo(() => [
    { id: "all", label: "All" },
    { id: "pending", label: "Pending" },
    { id: "confirmed", label: "Confirmed" },
    { id: "processed", label: "Processed" },
  ], []);

  const categoriesFilter: FilterCategory[] = useMemo(() => [
    {
      title: "Order Status",
      options: [
        { label: "Pending", count: 0 },
        { label: "Confirmed", count: 0 },
        { label: "Processed", count: 0 },
      ],
    },
    {
      title: "Total Amount",
      options: [
        { label: "Below $100", count: 0 },
        { label: "$100 - $300", count: 0 },
        { label: "Above $300", count: 0 },
      ],
    },
  ], []);

  const handleFilterChange = useCallback((selected: string[]) => {
    console.log("Selected Filters:", selected);
  }, []);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Typography variant="heading5" fontFamily="satoshi">
            Loading orders...
          </Typography>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = 'error' in error
      ? error.error
      : 'status' in error
        ? `Error: ${error.status}`
        : 'Failed to load orders';

    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Typography
            variant="heading5"
            fontFamily="satoshi"
            className={styles.errorMessage}
          >
            Error Loading Orders
          </Typography>
          <Typography variant="bodyRegular" fontFamily="satoshi">
            {errorMessage}
          </Typography>
          <button
            onClick={() => refetch()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#1D1D1C',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: 'Satoshi',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableTop}>
        <div className={styles.searchContainer}>
          <img src={searchIcon} alt="Search icon" />
          <input
            type="text"
            placeholder='Search by Order ID or Customer'
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className={styles.filterContainer}>
          <button
            title="Filter"
            aria-label="Filter orders"
            className={styles.btnIcon}
            onClick={() => { setFilterToggle(prev => !prev) }}
          >
            <img src={filter} alt="Filter icon" />
          </button>

          {filterToggle && categoriesFilter && (
            <div className={styles.filterDropdown}>
              <FilterDropdown
                categories={categoriesFilter}
                onChange={handleFilterChange}
                classname={styles.dropdownMenu}
              />
            </div>
          )}
        </div>

        <button title="Export" aria-label="Export orders" className={styles.btnIcon}>
          <img src={exportIcon} alt="Export icon" />
        </button>
      </div>

      <div className={styles.categoryContainer}>
        <CategorySelector
          categories={categories}
          defaultSelected="all"
          onSelect={(id) => {
            setSelectedCategory(id);
            setCurrentPage(1);
          }}
        />
      </div>

      {data.length > 0 ? (
        <>
          <div className={styles.tableWrapper}>
            <div className={styles.tableContainer}>
              <SellerTable
                header={columns}
                data={data}
                filters={categoriesFilter}
                searchFor={search}
                addModal={handleAddModal}
                orderModal={(id: string) => {
                  handleOrderModal();
                  setOrderId(id);
                }}
              />
            </div>
          </div>

          <div className={styles.moblieView}>
            {data.map((item) => (
              <OrderList
                key={item.fullId}
                id={item.id}
                address={item.customer}
                item={item.item}
                date={item.created}
                price={item.total}
                status={item.status}
                onClick={() => {
                  handleOrderModal();
                  setOrderId(item.fullId);
                }}
              />
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Typography variant="bodyRegular" fontFamily="satoshi">
            No orders found. {search ? 'Try a different search term.' : ''}
          </Typography>
        </div>
      )}

      <DeleteProductModal
        open={openDeleteModal}
        toggle={handleDeleteModal}
      />

      <AddProductListModal
        isOpen={openAddProductModal}
        onClose={handleAddModal}
        onAddProduct={() => { }}
      />

      <OrderModal
        id={orderId}
        openModal={orderModal}
        toggle={handleOrderModal}
      />
    </div>
  );
};